import { Action } from 'redux';
import { DRXRetrieveSingleProductResponse, DRXRetrieveAllProductsItem, DRXGraphAndTableData, DRXMonthlyCosts, DRXRetailAndMailCosts } from 'src/app/shared/interfaces/drx.interface';
import { DRUG_TOGGLE_OPTIONS } from 'src/app/shared/enums/drug-costs.enum';
import { County } from '../../../shared/interfaces/zip-county-response.interface';

/** 3 service calls populate this:
 * 1) provides a session Id
 * 2) consumes session id to provide plans
 * 3) consumes session id and individual mapped plan id to return single product data
 */
export interface DRXStateStructure {
  drxSessionId: string;
  drxAllProductsResponse: DRXRetrieveAllProductsItem[];
  drxSingleProductResponse: DRXRetrieveSingleProductResponse;
  drxGraphData: DRXGraphAndTableData;
  drxMonthlyCosts: DRXRetailAndMailCosts;
  drxToggleOption: DRUG_TOGGLE_OPTIONS;
  drxSelectedPlanContractId: string;
  drxPreviousEncodedSessionMeds: string;
  drxPreviousEncodedDetailMeds: string;
  drxSessionStartTimeInMS: number;
  effectiveDateStored: string;
  countyStored: County;
}

export const INITIAL_DRX_STATE: DRXStateStructure = {
  drxSessionId: '',
  drxAllProductsResponse: null,
  drxSingleProductResponse: null,
  drxGraphData: null,
  drxMonthlyCosts: {
    mailOrder: null,
    retail: null
  },
  drxToggleOption: DRUG_TOGGLE_OPTIONS.RETAIL,
  drxSelectedPlanContractId: null,
  drxPreviousEncodedSessionMeds: null,
  drxPreviousEncodedDetailMeds: null,
  drxSessionStartTimeInMS: null,
  effectiveDateStored: null,
  countyStored: null
};

export class DrxAction implements Action {
  type: string;
  drxSessionId?: string;
  drxAllProductsResponse?: DRXRetrieveAllProductsItem[];
  drxSingleProductResponse?: DRXRetrieveSingleProductResponse;
  drxGraphData?: DRXGraphAndTableData;
  drxMonthlyMailOrderCosts?: DRXMonthlyCosts;
  drxMonthlyRetailCosts?: DRXMonthlyCosts;
  drxToggleOption?: DRUG_TOGGLE_OPTIONS;
  drxSelectedPlanContractId?: string;
  drxPreviousEncodedSessionMeds?: string;
  drxPreviousEncodedDetailMeds?: string;
  drxSessionStartTimeInMS?: number;
  effectiveDateStored?: string;
  countyStored?: County
}

/** Action Types */
const SET_SESSION_ID = 'SET_SESSION_ID';
const SET_DRX_ALL_PRODUCTS_RESPONSE = 'SET_DRX_ALL_PRODUCTS_RESPONSE';
const SET_DRX_SINGLE_PRODUCT_RESPONSE = 'SET_DRX_SINGLE_PRODUCT_RESPONSE';
const SET_COST_DATA = 'SET_COST_DATA';
const SET_COUNTY_STORED = 'SET_COUNTY_STORED';
const TOGGLE_MAILORDER_OR_RETAIL = 'TOGGLE_MAILORDER_OR_RETAIL';
const SET_SELECTED_PLAN_CONTRACT_ID = 'SET_SELECTED_PLAN_CONTRACT_ID';
const SET_PREVIOUS_SESSION_MEDS = 'SET_PREVIOUS_SESSION_MEDS';
const SET_PREVIOUS_DETAIL_MEDS = 'SET_PREVIOUS__DETAIL_MEDS';
const SET_EFFECTIVE_DATE_STORED = 'SET_EFFECTIVE_DATE_STORED';


/** Actions */

export function setSessionId(drxSessionId: string): DrxAction {
  return { type: SET_SESSION_ID, drxSessionId };
}
export function setEffectiveDateStored(effectiveDateStored: string): DrxAction {
  return { type: SET_EFFECTIVE_DATE_STORED, effectiveDateStored };
}
export function setCountyStored(countyStored: County): DrxAction {
  return { type: SET_COUNTY_STORED, countyStored };
}
export function setDrxAllProductsResponse(drxAllProductsResponse: DRXRetrieveAllProductsItem[]): DrxAction {
  return { type: SET_DRX_ALL_PRODUCTS_RESPONSE, drxAllProductsResponse };
}
export function setDrxSingleProductResponse(drxSingleProductResponse: DRXRetrieveSingleProductResponse): DrxAction {
  return { type: SET_DRX_SINGLE_PRODUCT_RESPONSE, drxSingleProductResponse };
}
export function setCostsData(drxGraphData: DRXGraphAndTableData, drxMonthlyMailOrderCosts: DRXMonthlyCosts, drxMonthlyRetailCosts: DRXMonthlyCosts): DrxAction {
  return { type: SET_COST_DATA, drxGraphData, drxMonthlyMailOrderCosts, drxMonthlyRetailCosts };
}
export function toggleMailOrderOrRetail(drxToggleOption: DRUG_TOGGLE_OPTIONS) {
  return { type: TOGGLE_MAILORDER_OR_RETAIL, drxToggleOption };
}
export function setSelectedPlanContractId(drxSelectedPlanContractId: string) {
  return { type: SET_SELECTED_PLAN_CONTRACT_ID, drxSelectedPlanContractId };
}
export function setPreviousEncodedSessionMeds(drxPreviousEncodedSessionMeds: string, drxSessionStartTimeInMS: number) {
  return { type: SET_PREVIOUS_SESSION_MEDS, drxPreviousEncodedSessionMeds, drxSessionStartTimeInMS };
}
export function setPreviousEncodedDetailMeds(drxPreviousEncodedDetailMeds: string) {
  return { type: SET_PREVIOUS_DETAIL_MEDS, drxPreviousEncodedDetailMeds };
}

export function DrxServiceData(
  state: DRXStateStructure = INITIAL_DRX_STATE,
  action: DrxAction
) {

  const copiedState: DRXStateStructure = Object.assign({}, state);

  switch (action.type) {

    case SET_SESSION_ID:
      copiedState.drxSessionId = action.drxSessionId;
      return Object.assign({}, state, copiedState);
      
    case SET_EFFECTIVE_DATE_STORED:
      copiedState.effectiveDateStored = action.effectiveDateStored;
      return Object.assign({}, state, copiedState);

    case SET_COUNTY_STORED:
      copiedState.countyStored = action.countyStored;
      return Object.assign({}, state, copiedState);

    case SET_DRX_ALL_PRODUCTS_RESPONSE:
      copiedState.drxAllProductsResponse = action.drxAllProductsResponse;
      return Object.assign({}, state, copiedState);

    case SET_DRX_SINGLE_PRODUCT_RESPONSE:
      copiedState.drxSingleProductResponse = action.drxSingleProductResponse;
      return Object.assign({}, state, copiedState);

    case SET_COST_DATA:
      copiedState.drxGraphData = action.drxGraphData;
      copiedState.drxMonthlyCosts.mailOrder = action.drxMonthlyMailOrderCosts;
      copiedState.drxMonthlyCosts.retail = action.drxMonthlyRetailCosts;
      return Object.assign({}, state, copiedState);

    case TOGGLE_MAILORDER_OR_RETAIL:
      copiedState.drxToggleOption = action.drxToggleOption;
      return Object.assign({}, state, copiedState);

    case SET_SELECTED_PLAN_CONTRACT_ID:
      copiedState.drxSelectedPlanContractId = action.drxSelectedPlanContractId;
      return Object.assign({}, state, copiedState);

    case SET_PREVIOUS_SESSION_MEDS:
      copiedState.drxPreviousEncodedSessionMeds = action.drxPreviousEncodedSessionMeds;
      copiedState.drxSessionStartTimeInMS = action.drxSessionStartTimeInMS;
      return Object.assign({}, state, copiedState);

    case SET_PREVIOUS_DETAIL_MEDS:
      copiedState.drxPreviousEncodedDetailMeds = action.drxPreviousEncodedDetailMeds;
      return Object.assign({}, state, copiedState);

    default:
      const defaultAndSortState = Object.assign({}, state);
      return defaultAndSortState;
  }
}
